import { useMemo } from 'react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { addMonths, startOfWeek } from 'date-fns';

import { api } from '@api';
import {
  ReservationType,
  SaunaShift,
  SaunaWithReservations,
} from '@models/sauna';
import { useApartment } from '@stores/apartmentStore';
import { authStore } from '@stores/authStore';
import { formatSerializedPlainDate } from '@utils/date.utils';

export const createSaunasQueryKey = (apartmentId: string) =>
  ['real-estate-saunas', apartmentId] satisfies QueryKey;

export const createSaunaReservationsQueryKey = (apartmentId: string) =>
  ['sauna-reservations', apartmentId] satisfies QueryKey;

export const fetchApartmentSaunas = async (apartmentId: string | undefined) => {
  if (!apartmentId) throw Error('Apartment ID required');

  // Causes infinite query loop without this try-catch. Setting `retry: false` for useQuery does not solve the issue.
  try {
    return (await api.sauna.getSaunas(apartmentId)).data;
  } catch (e) {
    return [];
  }
};

/** Utility hook for obtaining saunas for a given apartment (@defaults to selected apartment)
 * that can be reserved through OmaSATO. Sauna reservation data is also included in the sauna
 * instances */
export const useOmaSATOSaunas = (
  /** Explicit apartment ID, fallback to selected apartment ID in global state */
  id?: string
) => {
  const { apartment } = useApartment(id);

  const apartmentId = apartment?.apartmentId;

  /* check authStore.isAuthenticated state on re-renders but don't trigger
   * useQuery on authStore.isAuthenticated change (since it would require wrapping
   * the hook inside an observer somehow). This is because useQuery hooks are only
   * invoked when the user is accessing an authenticated route, i.e. has authenticated
   * already. However, when logging-out, authStore.isAuthenticated state changes before
   * unmounting the authenticated routes and, therefore, up-to-date authentications
   * state must be checked to avoid illegal API calls on log-out */
  const isQueriesEnabled = !!apartmentId && authStore.isAuthenticated;

  const saunasQuery = useQuery(
    createSaunasQueryKey(apartmentId ?? ''),
    () => fetchApartmentSaunas(apartmentId),
    { enabled: isQueriesEnabled }
  );

  // This is here because it fetches all reservations for every sauna in the property
  const saunaReservationsQuery = useQuery(
    createSaunaReservationsQueryKey(apartmentId ?? ''),
    async () => {
      if (!apartmentId) return;

      const start = startOfWeek(new Date(), { weekStartsOn: 1 });
      const end = addMonths(start, 7);

      const startDate = formatSerializedPlainDate(start);
      const endDate = formatSerializedPlainDate(end);

      try {
        const response = await api.sauna.getSaunaReservations({
          apartmentId,
          startDate,
          endDate,
        });

        // If response does not return an array, user has no reservable saunas
        if (!Array.isArray(response.data)) {
          return [];
        }

        return response.data;
      } catch {
        return [];
      }
    },
    { enabled: isQueriesEnabled }
  );

  return useMemo(() => {
    const { data, ...reservationQueryProps } = saunaReservationsQuery;

    const saunasWithReservations =
      saunasQuery.data?.map<SaunaWithReservations>((sauna) => {
        const { reservations, sharedShifts } =
          data?.reduce<{
            reservations: SaunaShift[];
            sharedShifts: SaunaShift[];
          }>(
            (prev, curr) => {
              if (
                sauna.resourceId !== curr.resourceId ||
                curr.type === ReservationType.NOT_RESERVABLE
              )
                return prev;

              return curr.type === ReservationType.CONTINUOUS_PUBLIC_RESERVATION
                ? { ...prev, sharedShifts: [...prev.sharedShifts, curr] }
                : { ...prev, reservations: [...prev.reservations, curr] };
            },
            { reservations: [], sharedShifts: [] }
          ) ?? {};

        return {
          ...sauna,
          reservations: reservations ?? [],
          sharedShifts: sharedShifts ?? [],
        };
      }) ?? [];

    return {
      ...reservationQueryProps,
      saunas: saunasWithReservations,
      isError: saunasQuery.isError || reservationQueryProps.isError,
      isSuccess: saunasQuery.isSuccess && reservationQueryProps.isSuccess,
      isFetched: saunasQuery.isFetched && reservationQueryProps.isFetched,
      isLoading:
        // set isLoading to false if queries are not enabled
        isQueriesEnabled &&
        (saunasQuery.isLoading || reservationQueryProps.isLoading),
    };
  }, [saunaReservationsQuery, saunasQuery]);
};
